import { useCallback, useState } from 'react';
import { Header, LwButton, LWModal, Page, Toolbox } from 'redesign';
import { FilterButton } from 'ogp/components/views/administration/shared';
import { useExternalParams, useToggle } from 'shared/hooks';
import { useInvalidateQuery } from 'shared/hooks/query-hooks/useInvalidateQuery';
import { QUERY_KEYS } from 'api/query-keys';
import { PartnerPlacementsDataProvider } from './partner-placements-table';
import { useCancelShiftPlacements } from './hooks/use-cancel-shift-placements';
import { PLACEMENTS_INITIAL_VALUES } from './partner-placements.types';
import { usePlacementsPage } from './hooks/use-placements-page';
import { partnerPlacementAlerts, useAlert } from '../../../../shared/components/alerts';
import { IconTrash } from '../../../../assets/img';
import { ShiftPlanningModal } from '../partner-shift-planning/partner-shift-planning-modal/partner-shift-planning-modal';

const PartnerPlacements = () => {
  const { filters: appliedFilters, setFilters, selectedFilterCount } = usePlacementsPage();
  const { on: filtersOpen, setOn, setOff } = useToggle();
  const [params, setParams] = useExternalParams(PLACEMENTS_INITIAL_VALUES);

  const [selection, setSelection] = useState<string[]>([]);
  const onSelectionChange = useCallback((selectedIds: string[]) => {
    setSelection(selectedIds);
  }, []);
  const { alertSuccess, alertError } = useAlert();
  const { mutate } = useCancelShiftPlacements();
  const invalidateQuery = useInvalidateQuery();
  const cancelPlacements = useCallback(
    () =>
      mutate(
        { placementIds: selection },
        {
          onSuccess: () => {
            invalidateQuery(QUERY_KEYS.placements());
            alertSuccess(partnerPlacementAlerts.success.bulkDeleted);
          },
          onError: (e) => alertError(e),
        }
      ),
    [alertError, alertSuccess, mutate, selection]
  );
  const openShiftDetail = useCallback(
    (shiftId: string) => {
      setParams({ shiftId });
    },
    [setParams]
  );

  const closeShiftDetail = useCallback(() => {
    setParams({ shiftId: undefined });
  }, [setParams]);

  return (
    <Page
      header={<Header titleText="Plaatsingen" />}
      toolbox={
        <Toolbox>
          <LwButton
            disabled={selection.length === 0}
            type="button"
            color="primary"
            onClick={cancelPlacements}
            startIcon={<IconTrash />}
          >
            Annuleer alle plaatsingen
          </LwButton>
          <Toolbox.PushRight>
            <FilterButton onClick={setOn} nrOfActiveFilters={selectedFilterCount} />
          </Toolbox.PushRight>
        </Toolbox>
      }
    >
      <PartnerPlacementsDataProvider
        onSelectionChange={onSelectionChange}
        appliedFilters={appliedFilters}
        setFilters={setFilters}
        filtersOpen={filtersOpen}
        onClose={setOff}
        openShiftDetail={openShiftDetail}
      />
      <LWModal isOpen={params.shiftId != null} onClose={closeShiftDetail}>
        {params.shiftId ? (
          <ShiftPlanningModal shiftId={params.shiftId} closeShiftDetail={closeShiftDetail} />
        ) : null}
      </LWModal>
    </Page>
  );
};

export { PartnerPlacements };
